import React from 'react';
import { Link } from 'react-router-dom';

export type DashIconAction = 'add' | 'edit' | 'delete' | 'manage' | 'back' | 'confirm';

interface DashIconButtonProps {
  action: DashIconAction;
  title: string;
  'aria-label'?: string;
  to?: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}

const ICONS: Record<DashIconAction, React.ReactNode> = {
  add: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
  edit: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 20h4l10.5-10.5a2.12 2.12 0 0 0-3-3L5 17v3z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.5 6.5l3 3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  delete: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  manage: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 5l7 7-7 7"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  back: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 5l-7 7 7 7"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  confirm: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12l5 5L20 7"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

const DashIconButton: React.FC<DashIconButtonProps> = ({
  action,
  title,
  'aria-label': ariaLabel,
  to,
  onClick,
  className = '',
  disabled = false,
}) => {
  const classes = `dash-icon-btn dash-icon-btn-${action} ${className}`.trim();
  const label = ariaLabel || title;

  if (to && !disabled) {
    return (
      <Link to={to} className={classes} title={title} aria-label={label}>
        {ICONS[action]}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      title={title}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
    >
      {ICONS[action]}
    </button>
  );
};

export default DashIconButton;
